<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-step
        :complete="SEND_CODE.complete"
        :step="SEND_CODE.index"
        :rules="SEND_CODE.errors"
      >
        Send verification code
      </v-stepper-step>
      <v-divider />
      <v-divider />
      <v-stepper-step :step="ENTER_CODE.index">
        Enter verification code
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <mfa-step
        :step="SEND_CODE"
        :data-testid="SEND_CODE.id"
        full-width
      >
        <template #title>
          Send verification code
        </template>
        <template #subtitle>
          You need to complete the reCAPTCHA in order to send your verification code
        </template>
        <verify-mfa-send-code
          :resolver="resolver"
          @codeSent="sendCodeCompleted"
          @error="handleError"
        />
      </mfa-step>

      <mfa-step
        :step="ENTER_CODE"
        :data-testid="ENTER_CODE.id"
        full-width
      >
        <template #title>
          Enter verification code
        </template>
        <template #subtitle>
          Please enter the verification code you received by SMS to your
          nominated phone number
        </template>
        <verify-mfa-enter-code
          :verification-id="verificationId"
          :resolver="resolver"
          @mfaVerified="enterCodeCompleted"
          @error="handleError"
        />
      </mfa-step>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { createStepData } from '@/utils/steppers'
import {
  AUTH_CAPTCHA_EXPIRED,
  AUTH_CODE_EXPIRED,
  AUTH_INVALID_MULTI_FACTOR_SESSION,
  AUTH_INVALID_VERIFICATION_CODE,
  AUTH_TOO_MANY_REQUESTS
} from '@/utils/auth'
import MfaStep from '../MfaStep.vue'
import VerifyMfaSendCode from './VerifyMfaSendCode.vue'
import VerifyMfaEnterCode from './VerifyMfaEnterCode.vue'

const SEND_CODE = 'SEND_CODE'
const ENTER_CODE = 'ENTER_CODE'

export default {
  name: 'EnableMfa',
  components: {
    MfaStep,
    VerifyMfaSendCode,
    VerifyMfaEnterCode
  },
  props: {
    mfaError: { type: Error, required: true }
  },
  emits: ['mfaVerified'],
  data: function () {
    return {
      currentStep: 1,
      verificationId: null,
      ...createStepData(SEND_CODE, ENTER_CODE)
    }
  },
  computed: {
    resolver() {
      return this.mfaError.resolver
    }
  },
  methods: {
    async sendCodeCompleted(verificationId) {
      this.verificationId = verificationId
      this.completeStep(SEND_CODE)
      this.setCurrentStep(ENTER_CODE)
    },
    async enterCodeCompleted(verificationResult) {
      this.$emit('mfaVerified', verificationResult.user)
    },
    handleError(error) {
      switch (error.code) {
      case AUTH_CAPTCHA_EXPIRED:
        if (!this.verificationId) {
          this.addErrorMessage(
            SEND_CODE,
            'The reCAPTCHA has expired. You will have to complete it again.'
          )
        }
        break
      case AUTH_CODE_EXPIRED:
        this.addErrorMessage(
          SEND_CODE,
          'Your verification code has expired. You will have to send a new one.'
        )
        break
      case AUTH_INVALID_VERIFICATION_CODE:
        this.addErrorMessage(ENTER_CODE, 'Invalid verification code.')
        break
      case AUTH_INVALID_MULTI_FACTOR_SESSION:
        this.addErrorMessage(SEND_CODE, 'Your multi-factor session has expired. Please refresh the page and re-authenticate.')
        break
      case AUTH_TOO_MANY_REQUESTS:
        this.addErrorMessage(
          SEND_CODE,
          'Requests from this device have been blocked due to unusual activity. Try again later.'
        )
        break
      default:
        throw error
      }
    },
    addErrorMessage(stepId, message) {
      this.step(stepId).errors = [() => message]
      this.setCurrentStep(stepId)
    },
    completeStep(stepId) {
      const step = this.step(stepId)
      step.complete = true
      step.errors = []
    },
    setCurrentStep(stepId) {
      this.currentStep = this.step(stepId).index
    },
    step(stepId) {
      return this[stepId]
    }
  }
}
</script>
